/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/prefer-for-of */
import { message, Radio, Row } from 'antd';
import { types as CardType } from 'credit-card-type';
import { useFormik } from 'formik';
import React, { ReactElement, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isIOS, isMacOs, isSafari } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import * as yup from 'yup';

import ApplePayLogo from '~/assets/logos/apple-pay.png';
import GooglePayLogo from '~/assets/logos/google-pay.png';
import GimapayLogo from '~/assets/logos/logo-gimacpay.png';
import MastercardLogo from '~/assets/logos/mastercard-logo.png';
import MobileMoneyLogo from '~/assets/logos/mobile-money.png';
import VisaLogo from '~/assets/logos/visa-logo.png';
import Card from '~/components/Card/Card';
import BankOtpForm from '~/components/forms/BankOtpForm/BankOtpForm';
import BankPaymentMethodForm from '~/components/forms/BankPaymentMethodForm/BankPaymentMethodForm.component';
import GimacForm from '~/components/forms/GimacForm/GimacForm';
import MobilePaymentForm from '~/components/forms/MobilePaymentForm/MobilePaymentForm';
import PaymentMethodForm from '~/components/forms/PaymentMethodForm/PaymentMethodForm.component';
import { BankIcon } from '~/components/icons/BankIcon';
import Loader from '~/components/Loader/Loader';
import { Text } from '~/components/Typo/Typo';
import { OrderSummaryCardComponent } from '~/containers/OrderSummaryCard/OrderSummaryCard.component';
import { GetPaymentResponse } from '~/entities/responses/getPaymentResponse';
import { GetPostPaymentResponse } from '~/entities/responses/getPostPaymentResponse';
import { GetTransactionBankResponse } from '~/entities/responses/getTransactionBankResponse';
import { AvailableBank } from '~/entities/schemas/availableBank';
import { AvailableCountry } from '~/entities/schemas/availableCountry';
import { DataTransacton } from '~/entities/schemas/dataTransaction';
import { GimacCountry, GimacParticipant } from '~/entities/schemas/gimacCountry';
import { MeansOfPayment } from '~/entities/schemas/meansOfPayment';
import { GeneralException } from '~/exceptions/generalException';
import { Regex } from '~/helpers/regex';
import { ABANDONED_PAGE_ROUTE, ERROR_PAGE_ROUTE } from '~/routing/routes';
import { CryptoService } from '~/services/CryptoService';
import {
  GimacRequestToPayBody,
  InitBankTransferBody,
  PaymentBankTransferBody,
  PaymentService,
  ProcessPaymentBody,
} from '~/services/PaymentService';
import { FontWeight } from '~/theme/constants';
import { creditCardDetails } from '~/utils/credit-card-details';
import { logger } from '~/utils/func/logger';
import { isTransactionNotFoundError } from '~/utils/paymentResultUtils';
import { useBankOTPSchema } from '~/utils/schemas/bank-otp-schema';
import { useBankPaymentSchema } from '~/utils/schemas/bank-payment-schema';
import { useCreditCardPaymentSchema } from '~/utils/schemas/credit-card-payment-schema';
import { useExpressUnionSchema } from '~/utils/schemas/express-union-schema';
import { useGimacPaymentSchema } from '~/utils/schemas/gimac-payment-schema';
import { useMobileMoneyPaymentSchema } from '~/utils/schemas/mobile-money-payment-schema';

import styles from './OrderDetails.module.scss';

interface OrderDetailsComponentProps {
  handleSubmit: (
    values: any,
    payment?: MeansOfPayment,
    specificType?: string,
    selectedCountry?: AvailableCountry,
  ) => void;
  paymentInfos: GetPaymentResponse;
  loading?: boolean;
  processingMobilePayment?: boolean;
  handleSubmitMobilePaymentForm: (values: any, payment: MeansOfPayment) => void;
  dataTransaction: DataTransacton | null;
  loadingDataTr: boolean;
  initialPaymentMethod?: string;
  onBackToMethodSelection?: () => void;
  onNavigateToConfirmation?: () => void;
}

export type PaymentMethodType = {
  title: string;
  icons: Array<ReactElement<HTMLImageElement>>;
  component: ReactNode;
  type: string;
  specificType: string;
  hide?: boolean;
};

export const OrderDetailsComponent: React.FC<OrderDetailsComponentProps> = ({
  handleSubmit,
  paymentInfos,
  loading,
  processingMobilePayment,
  handleSubmitMobilePaymentForm,
  dataTransaction,
  loadingDataTr,
  initialPaymentMethod,
  onBackToMethodSelection,
  onNavigateToConfirmation,
}) => {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState<string>('');
  const [meanOfPayment, setMeanOfPayment] = useState<MeansOfPayment>();
  const [bankMeanOfPayment, setBankMeanOfPayment] = useState<MeansOfPayment>();
  const [processingGoogle, setProcessingGoogle] = useState<boolean>(false);
  const [processingApple, setProcessingApple] = useState<boolean>(false);
  const [googlePaymentResponse, setGooglePaymentResponse] = useState<GetPostPaymentResponse>();
  const [applePaymentResponse, setApplePaymentResponse] = useState<GetPostPaymentResponse>();
  const [filteredPaymentMethods, setFilteredPaymentMethods] = useState<PaymentMethodType[]>([]);
  const [filteringPayMethods, setFilteringPayMethods] = useState<boolean>(false);
  // const [transaction, setTransaction] = useState<GetPaymentResponse>();

  const { creditCardPaymentSchema } = useCreditCardPaymentSchema();
  const { gimacPaymentSchema } = useGimacPaymentSchema();
  const { mobileMoneyPaymentSchema } = useMobileMoneyPaymentSchema();
  const [mobileMeanOfPayment, setMobileMeanOfPayment] = useState<MeansOfPayment>();
  const [availableCountries, setAvailableCountries] = useState<AvailableCountry[]>([]);
  const [gettingCountries, setGettingCountries] = useState<boolean>(false);
  const [countriesRefact, setCountriesRefact] = useState<string[]>([]);
  const [selectedAvailableCountry, setSelectedAvailableCountry] = useState<AvailableCountry>();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [dialCode, setDialCode] = useState<string>('');

  const [gimacCountries, setGimacCountries] = useState<GimacCountry[]>([]);
  const [loadingGimacCountries, setLoadingGimacCountries] = useState<boolean>(false);
  const [detectedGimacOperator, setDetectedGimacOperator] = useState<GimacParticipant | null>(null);
  const [processingGimac, setProcessingGimac] = useState<boolean>(false);
  const [gimacWaitingValidation, setGimacWaitingValidation] = useState<boolean>(false);

  const { bankPaymentSchema } = useBankPaymentSchema();
  const { expressUnionSchema } = useExpressUnionSchema();
  const { bankOtpSchema } = useBankOTPSchema();
  const [availableBank, setAvailableBank] = useState<AvailableBank[]>([]);
  const [gettingBank, setGettingBank] = useState<boolean>(false);
  const [bankRefact, setBankRefact] = useState<string[]>([]);
  const [selectedAvailableBank, setSelectedAvailableBank] = useState<AvailableBank>();
  const [load, setLoad] = useState<boolean>(false);
  const [loadExpressUnion, setLoadExpressUnion] = useState<boolean>(false);
  const [showOtp, setShowOtp] = useState<boolean>(false);
  const [showChoiceAccount, setShowChoiceAccount] = useState<boolean>(false);
  const [loadOtp, setLoadOtp] = useState<boolean>(false);
  const [showMessageInit, setShowMessageInit] = useState<boolean>(false);
  const [showMessageOtp, setShowMessageOtp] = useState<boolean>(false);
  const [showMessageError, setShowMessageError] = useState<boolean>(false);
  const [hideTimer, setHideTimer] = React.useState(false);
  const [loadWaitActionView, setLoadWaitActionView] = useState<boolean>(false);
  const [startRebours, setStartRebours] = useState<number>(9999);
  const [disableButtonPay, setDisableButtonPay] = useState<boolean>(true);
  const [disableLoadingPay, setDisableLoadingPay] = useState<boolean>(false);
  // const history = useHistory();
  const { push } = useHistory();

  const getTransactionDetails = useCallback(
    async (transactionRef: string) => {
      try {
        const res = await PaymentService.getTransactionDetails(transactionRef);
        const {
          data,
          data: {
            transaction: { status },
          },
        } = res;

        if (data !== null && data !== undefined) {
          switch (status) {
            case 'ACCEPTED':
              window.location.reload(); // push(SUCCESS_PAGE_ROUTE());
              break;
            case 'PAYMENT_ALREADY_PROCESSED':
              window.location.reload(); // Paiement déjà effectué
              break;
            case 'REFUSED': // REFUSED
              window.location.reload(); // push(FAIL_PAGE_ROUTE());
              break;
            case 'REJECTED': // REJECTED
              window.location.reload(); // push(FAIL_PAGE_ROUTE());
              break;
            case 'PENDING': {
              if (loadWaitActionView === false) {
                setLoadExpressUnion(false);
                setLoadWaitActionView(true);
                setStartRebours(5);
              }
              if (selectedMethod === 'gimac') {
                setDisableButtonPay(false);
                setDisableLoadingPay(false);
              }
              onNavigateToConfirmation?.();
              break;
            }
            default:
              push(ERROR_PAGE_ROUTE());
              break;
          }
        }
      } catch (error: unknown) {
        if (isTransactionNotFoundError(error)) {
          push(ABANDONED_PAGE_ROUTE());
          return;
        }
        const exception = (error as { response?: { data?: GeneralException } })?.response?.data as GeneralException;
        if (process.env.NODE_ENV === 'development') {
          logger.debug('exception', { prefix: 'OrderDetails', data: exception });
        }
        message.error(exception?.description || t('home:global:generalResponse'), 1);
      }
    },
    [onNavigateToConfirmation, push],
  );

  type FlowStatePayBank = {
    bankAccount: string;
    token: string;
    dateOfBirth?: string;
  };
  const [flowStatePayBank, setFlowStatePayBank] = useState<FlowStatePayBank>({
    bankAccount: '',
    token: '',
    dateOfBirth: '',
  });

  type CreditCardPaymentType = yup.InferType<typeof creditCardPaymentSchema>;

  type GimacPaymentType = yup.InferType<typeof gimacPaymentSchema>;

  type MobileMoneyPaymentType = yup.InferType<typeof mobileMoneyPaymentSchema>;

  type BankPaymentType = yup.InferType<typeof bankPaymentSchema>;

  type BankOtpType = yup.InferType<typeof bankOtpSchema>;

  type ExpressUnionPaymentType = yup.InferType<typeof expressUnionSchema>;

  const regex = new Regex(navigator);

  const handleGetAvailableCountries = useCallback(async () => {
    setGettingCountries(true);
    try {
      const res = await PaymentService.getAvailableCountries();
      const finalCountries = [];
      for (const c of res) {
        finalCountries.push(c.countrycode.toLowerCase());
      }
      setCountriesRefact(finalCountries);
      setAvailableCountries(res);
      setGettingCountries(false);
    } catch (error: any) {
      setGettingCountries(false);
      message.error('Impossible de charger la liste des pays disponibles pour le mobile money', 5);
    }
  }, []);

  const handleGetGimacCountries = useCallback(async () => {
    setLoadingGimacCountries(true);
    try {
      const res = await PaymentService.getGimacCountries();
      setGimacCountries(res);
      setLoadingGimacCountries(false);
    } catch (error: any) {
      setLoadingGimacCountries(false);
      message.error('Impossible de charger la liste des pays GIMAC', 5);
    }
  }, []);

  const handleGetAvailableBanks = useCallback(async () => {
    setGettingBank(true);
    try {
      const res = await PaymentService.getAvailableBanks();
      const finalBanks = [];
      for (const b of res) {
        finalBanks.push(b.upicode.toLowerCase());
      }
      setBankRefact(finalBanks);
      setAvailableBank(res);
      setGettingBank(false);
    } catch (error: any) {
      setGettingBank(false);
      message.error('Impossible de charger la liste des banques disponibles pour le transfert bancaire', 5);
    }
  }, []);

  const handleGetAuthentification = useCallback(async () => {
    try {
      const res = await PaymentService.getTokenAuthenticatedBank();
      return res;
    } catch (error: any) {
      logger.error('Payment error', error instanceof Error ? error : undefined, {
        prefix: 'OrderDetails',
        data: error,
      });
    }
  }, []);

  const onSubmitCard = (values: CreditCardPaymentType) => {
    handleSubmit({ ...values, cardType }, meanOfPayment as MeansOfPayment, 'CARD');
  };

  const onSubmitBank = (values: BankPaymentType) => {
    setLoad(true);
    handleGetAuthentification()
      .then(responses => {
        setLoad(true);
        // Init transaction
        const content = {
          transactionRef: paymentInfos?.data.transactionRef as string,
          channelId: paymentInfos?.data.meansOfPayment.find(e => e.name === 'BANK TRANSFERT' && e.active === true)
            ?.id as string,
          paymentMethod: 'BANK',
          bankTransfert: {
            applicationCode: selectedAvailableBank?.applicationcode as string,
            countryCode: selectedAvailableBank?.countrycode as string,
            productCode: selectedAvailableBank?.productcode as string,
            currencyCode: selectedAvailableBank?.currencycode as string,
            upiCode: selectedAvailableBank?.upicode as string,
            bankAccount: values.bankAccount,
            token: responses?.token as string,
            initiator: {
              dateOfBirth: values.dateOfBirth,
            },
          },
        };
        setFlowStatePayBank({
          bankAccount: values.bankAccount,
          token: responses?.token as string,
          dateOfBirth: values.dateOfBirth,
        });
        // console.log(content, 'content'); // Init transaction
        PaymentService.processInitBankTransfer(content as InitBankTransferBody)
          .then(res => {
            setLoad(true);
            if (res.status === 'SUCCESS') {
              toast.success(t('otpForm:enterOtpCodeReceived'));
              setShowOtp(true);
              setShowMessageInit(false);
            } else {
              // console.log(res, 'err init bank transfert');
              setTimeout(() => {
                toast.success(t('bank:errorInit'));
                setLoad(false);
                setShowMessageInit(true);
              }, 500);
            }
          })
          .catch(err => {
            localStorage.removeItem('transactionDetails');
            setTimeout(() => {
              setLoad(false);
              setShowMessageInit(true);
            }, 500);
          });
      })
      .catch((error: any) => {
        setTimeout(() => {
          setLoad(false);
        }, 2000);
        localStorage.removeItem('transactionDetails');
        logger.error('Payment error', error instanceof Error ? error : undefined, {
          prefix: 'OrderDetails',
          data: error,
        });
      });
  };

  const onSubmitOtpBank = (values: BankOtpType) => {
    const { otp } = values;
    const code = otp.replace('GW-', '');
    setLoadOtp(true);
    const content_bank = {
      transactionRef: paymentInfos?.data.transactionRef as string,
      channelId: paymentInfos?.data.meansOfPayment.find(e => e.name === 'BANK TRANSFERT' && e.active === true)
        ?.id as string,
      paymentMethod: 'BANK',
      bankTransfert: {
        applicationCode: selectedAvailableBank?.applicationcode as string,
        countryCode: selectedAvailableBank?.countrycode as string,
        productCode: selectedAvailableBank?.productcode as string,
        currencyCode: selectedAvailableBank?.currencycode as string,
        upiCode: selectedAvailableBank?.upicode as string,
        bankAccount: flowStatePayBank.bankAccount,
        token: flowStatePayBank.token as string,
        initiator: {
          OTP: code,
        },
      },
    };
    PaymentService.processPaymentBankTransfer(content_bank as PaymentBankTransferBody)
      .then(res => {
        const dataTrans = res.data as string;
        const crypto = new CryptoService();
        crypto.setSecretKey(process.env.REACT_APP_APPLI_SECKEY ?? '');
        const decryptData: GetTransactionBankResponse = crypto.decryptData(dataTrans);
        // console.log(decryptData, 'decryptData bank transfert');
        setLoadOtp(true);
        if (decryptData.message === 'PAYMENT_ALREADY_PROCESSED') {
          alert('le paiement à déjà été effectué');
        }
        if (decryptData.data.status === 'ACCEPTED') {
          setShowOtp(true);
          setShowMessageOtp(false);
        } else {
          // console.log(res, 'err status REFUSED');
          setLoadOtp(false);
          setShowMessageOtp(true);
        }
        PaymentService.getTransactionDetails(decryptData.data.transactionRef as string)
          .then(() => {
            // in this case, we can redirect to the invoice page
            location.reload();
          })
          .catch((error: any) => {
            location.reload();
          });
      })
      .catch(err => {
        // console.log(err, 'err end');
        setTimeout(() => {
          setLoadOtp(false);
          setShowMessageOtp(true);
        }, 500);
      });
  };

  const onSubmitExpressUnion = (values: ExpressUnionPaymentType) => {
    setLoadExpressUnion(true);
    setDisableButtonPay(true);
    setDisableLoadingPay(true);
    // handleSubmit({ ...values, cardType }, meanOfPayment as MeansOfPayment, 'EUMM');
    // console.log(values, 'values express union');
    // console.log(
    //   paymentInfos?.data.meansOfPayment.find(e => e.name === 'OTHER' && e.active === true)?.id,
    //   'id methodpay',
    // );
    const content = {
      transactionRef: paymentInfos?.data.transactionRef as string,
      channelId: paymentInfos?.data.meansOfPayment.find(e => e.name === 'OTHER' && e.active === true)?.id as string,
      PaymentMethod: 'EUMM',
      mobileMoneyInfos: {
        PhoneNumber: values.indicatif + values.number,
      },
    };
    logger.debug('content express union', { prefix: 'OrderDetails', data: content });
    PaymentService.processPayment(content as ProcessPaymentBody)
      .then(res => {
        if (res.code === '662') {
          // Pending
          // getTransactionDetails(paymentInfos?.data.transactionRef as string);
          setInterval(() => {
            getTransactionDetails(paymentInfos?.data.transactionRef as string);
          }, 3000);
        }
        if (res.code === '600') {
          // Failed
          getTransactionDetails(paymentInfos?.data.transactionRef as string);
          setLoadExpressUnion(false);
        }
      })
      .catch(err => {
        setTimeout(() => {
          setLoadExpressUnion(false);
          logger.debug('une erreur est survenue', { prefix: 'OrderDetails' });
          push(ERROR_PAGE_ROUTE());
        }, 500);
      });
  };

  const askNewOtp = () =>
    new Promise((resolve, reject) => {
      setLoad(true);
      // Init transaction
      const content = {
        transactionRef: paymentInfos?.data.transactionRef as string,
        channelId: paymentInfos?.data.meansOfPayment.find(e => e.name === 'BANK TRANSFERT' && e.active === true)
          ?.id as string,
        paymentMethod: 'BANK',
        bankTransfert: {
          applicationCode: selectedAvailableBank?.applicationcode as string,
          countryCode: selectedAvailableBank?.countrycode as string,
          productCode: selectedAvailableBank?.productcode as string,
          currencyCode: selectedAvailableBank?.currencycode as string,
          upiCode: selectedAvailableBank?.upicode as string,
          bankAccount: flowStatePayBank.bankAccount,
          token: flowStatePayBank.token as string,
          initiator: {
            dateOfBirth: flowStatePayBank.dateOfBirth,
          },
        },
      };
      // console.log(content, 'content ask otp'); // ask otp
      PaymentService.processInitBankTransfer(content as InitBankTransferBody)
        .then(res => {
          setLoad(true);
          if (res.status === 'SUCCESS') {
            toast.success(t('otpForm:enterOtpCodeReceived'));
            setShowOtp(true);
            setShowMessageInit(false);
            resolve(res);
          } else {
            // console.log(res, 'err init bank transfert');
            setTimeout(() => {
              toast.success(t('bank:errorInit'));
              setLoad(false);
              setShowMessageInit(true);
              reject(res);
            }, 500);
          }
        })
        .catch(err => {
          localStorage.removeItem('transactionDetails');
          setTimeout(() => {
            setLoad(false);
            setShowMessageInit(true);
            reject(err);
          }, 500);
        });
    });

  const onSubmitMobile = (values: MobileMoneyPaymentType) => {
    // Concaténer l'indicatif au numéro pour la soumission (comme ExpressUnion)
    const currentDialCode = localStorage.getItem('countrySelectedOne') || '';
    const phoneNumberWithIndicatif = currentDialCode + values.phoneNumber;

    const submissionValues = {
      ...values,
      phoneNumber: phoneNumberWithIndicatif, // Numéro complet avec indicatif
    };

    handleSubmit(submissionValues, mobileMeanOfPayment, 'mobile', selectedAvailableCountry);
  };

  const onSubmitGimac = (values: GimacPaymentType) => {
    const currentDialCode = values.indicatif || '';
    // const walletSource = currentDialCode + values.phoneNumber;
    const walletSource = values.phoneNumber || '';

    const body: GimacRequestToPayBody = {
      intent: 'request_to_pay',
      issuerTrxRef: paymentInfos?.data.externalRef || '',
      walletSource,
      walletDestination: '',
      description: `Payment ${paymentInfos?.data.transactionRef || ''}`,
      amount: paymentInfos?.data.amount || 0,
      currency: paymentInfos?.data.currency || '',
      purchaseRef: paymentInfos?.data.transactionRef || '',
      toMember: detectedGimacOperator?.code || '',
      validityDuration: 0,
      createTime: Date.now(),
    };

    setProcessingGimac(true);
    setDisableButtonPay(true);
    setDisableLoadingPay(true);

    PaymentService.initGimacRequestToPay(body)
      .then(res => {
        logger.debug('GIMAC initRequestToPay response', { prefix: 'OrderDetails', data: res });
        setProcessingGimac(false);
        setDisableLoadingPay(false);
        setDisableButtonPay(true);
        setGimacWaitingValidation(true);
        setInterval(() => {
          getTransactionDetails(paymentInfos?.data.transactionRef as string);
        }, 3000);
      })
      .catch(err => {
        logger.error('GIMAC initRequestToPay error', err instanceof Error ? err : undefined, {
          prefix: 'OrderDetails',
          data: err,
        });
        setProcessingGimac(false);
        setDisableButtonPay(false);
        setDisableLoadingPay(false);
        const exception = err.response?.data as GeneralException;
        message.error(exception?.description || t('home:global:generalResponse'), 5);
      });
  };

  const {
    handleSubmit: handleFormSubmitBank,
    getFieldMeta: getFieldMetaBank,
    getFieldProps: getFieldPropsBank,
    handleChange: handleChangeBank,
    values: valuesBank,
    setFieldValue: setFieldValueBank,
  } = useFormik<BankPaymentType>({
    initialValues: {
      bankName: '',
      bankAccount: '',
      dateOfBirth: '',
    },
    onSubmit: onSubmitBank,
    validationSchema: bankPaymentSchema,
  });

  const {
    handleSubmit: handleFormSubmitOtpBank,
    getFieldMeta: getFieldMetaOtpBank,
    getFieldProps: getFieldPropsOtpBank,
    handleChange: handleChangeOtpBank,
    values: valuesOtpBank,
  } = useFormik<BankOtpType>({
    enableReinitialize: true,
    initialValues: {
      otp: '',
    },
    onSubmit: onSubmitOtpBank,
    validationSchema: bankOtpSchema,
  });

  const {
    handleSubmit: handleFormSubmitExpressUnion,
    getFieldMeta: getFieldMetaExpressUnion,
    getFieldProps: getFieldPropsExpressUnion,
    handleChange: handleChangeExpressUnion,
    values: valuesExpressUnion,
  } = useFormik<ExpressUnionPaymentType>({
    initialValues: {
      indicatif: '',
      number: '',
    },
    onSubmit: onSubmitExpressUnion,
    validationSchema: expressUnionSchema,
  });

  const {
    handleSubmit: handleFormSubmitCard,
    getFieldMeta,
    getFieldProps,
    handleChange,
    values,
    errors,
    touched,
    isValid,
  } = useFormik<CreditCardPaymentType>({
    initialValues: {
      cardNumber: '',
      cvv: '',
      expirationDate: '',
      cardHolder: '',
    },
    onSubmit: onSubmitCard,
    validationSchema: creditCardPaymentSchema,
    validateOnMount: true,
    validateOnChange: true,
  });

  const {
    handleSubmit: handleFormSubmitMobile,
    getFieldMeta: getFieldMetaMobile,
    getFieldProps: getFieldPropsMobile,
    handleChange: handleChangeMobile,
    values: valuesMobile,
    setFieldValue,
  } = useFormik<MobileMoneyPaymentType>({
    enableReinitialize: true,
    initialValues: {
      phoneNumber: paymentInfos?.data?.customerPhoneNumber ?? '',
      indicatif: ['225', '237'].includes(paymentInfos?.data?.customerZipCode?.replace(/\D/g, '') ?? '')
        ? (paymentInfos?.data?.customerZipCode?.replace(/\D/g, '') as string)
        : '',
    },
    onSubmit: onSubmitMobile,
    validationSchema: mobileMoneyPaymentSchema,
  });

  const {
    handleSubmit: handleFormSubmitGimac,
    getFieldMeta: getFieldMetaGimac,
    getFieldProps: getFieldPropsGimac,
    handleChange: handleChangeGimac,
    values: valuesGimac,
    setFieldValue: setFieldValueGimac,
    setFieldTouched: setFieldTouchedGimac,
  } = useFormik<GimacPaymentType>({
    enableReinitialize: true,
    initialValues: {
      phoneNumber: '',
      indicatif: '',
    },
    onSubmit: onSubmitGimac,
    validationSchema: gimacPaymentSchema,
  });

  const { type: cardType } = creditCardDetails(values.cardNumber);

  // Validation carte : calculée au render pour éviter désync avec Formik
  const isCardFormValid =
    (values.cardNumber?.replace(/\s/g, '')?.length ?? 0) >= 13 &&
    (values.cvv?.length ?? 0) >= 3 &&
    (values.expirationDate?.length ?? 0) >= 5 &&
    (values.cardHolder?.trim().length ?? 0) >= 2;

  // Gestion de la validation pour les cartes bancaires
  useEffect(() => {
    if (selectedMethod === 'CARD') {
      setDisableButtonPay(!(isValid || isCardFormValid));
    } else if (selectedMethod === 'gimac') {
      // Pour GIMAC, activer le bouton seulement quand le numero est renseigne
      const hasPhone = valuesGimac?.phoneNumber && valuesGimac.phoneNumber.length >= 3;
      const hasIndicatif = valuesGimac?.indicatif && valuesGimac.indicatif.length > 0;
      if (hasPhone && hasIndicatif) {
        setDisableButtonPay(false);
      } else {
        setDisableButtonPay(true);
      }
    } else if (selectedMethod && selectedMethod !== 'BANK') {
      // Pour tous les autres types de paiement (sauf BANK), activer le bouton
      setDisableButtonPay(false);
    }
  }, [selectedMethod, isValid, isCardFormValid, valuesGimac]);

  const initCardData = useCallback(() => {
    // console.log('paymentInfos', paymentInfos);
    const cardMeansOfPayment = paymentInfos?.data.meansOfPayment.filter(
      d => d.type === 'CARD' && d.active === true,
    ) as MeansOfPayment[];
    if (paymentInfos !== undefined) {
      // console.log(cardMeansOfPayment[0], 'cardMeansOfPayment[0] paymentInfos');
      // console.log(cardMeansOfPayment, 'cardMeansOfPayment paymentInfos');
      setMeanOfPayment(cardMeansOfPayment[0]);
      // Moyens de paiement carte configurés - bouton désactivé par défaut
    }
  }, [paymentInfos]);

  const initBankData = useCallback(() => {
    const bankMeansOfPayment = paymentInfos?.data.meansOfPayment.find(
      d => d.type === 'BANK' && d.active === true,
    ) as MeansOfPayment;
    if (paymentInfos !== undefined) {
      logger.debug('bankMeansOfPayment paymentInfos', { prefix: 'OrderDetails', data: bankMeansOfPayment });
      logger.debug('bankMeansOfPayment type', { prefix: 'OrderDetails', data: bankMeansOfPayment.type });
      setBankMeanOfPayment(bankMeansOfPayment);
      // setDisableButtonPay(true);
      // setDisableLoadingPay(false);
    }
  }, [paymentInfos]);

  const initMobileData = useCallback(() => {
    const getMobileMeanOfPayment = paymentInfos?.data.meansOfPayment.find(
      (m, i) => m.type === 'MOBILE_MONEY' && m.active === true && m.code === 'FLW_MM',
    ) as MeansOfPayment;
    if (paymentInfos !== undefined) {
      setMobileMeanOfPayment(getMobileMeanOfPayment);
      setDisableButtonPay(false);
    }
  }, [paymentInfos]);

  // Fonction pour activer automatiquement l'option Mobile Money - déclaration simple
  const activateMobileMoneyOption = useCallback(() => {
    logger.debug('activateMobileMoneyOption appelé - version simple', { prefix: 'OrderDetails' });
    setSelectedMethod('mobile');
    setDisableButtonPay(false);
    setDisableLoadingPay(false);
  }, []);

  const creditCardIcons: Array<ReactElement<HTMLImageElement>> = useMemo(() => {
    const options = [
      { img: <img src={VisaLogo} alt="visa" height={24} />, type: CardType.VISA },
      { img: <img src={MastercardLogo} alt="mastercard" height={24} />, type: CardType.MASTERCARD },
      // { img: <img src={AmexLogo} alt="amex" height={35} width={45} />, type: CardType.AMERICAN_EXPRESS },
      // { img: <img src={DiscovercardLogo} alt="discover card" height={32} />, type: CardType.DISCOVER },
      // { img: <img src={VerveLogo} alt="verve" height={24} />, type: CardType.MASTERCARD },
      // { img: <img src={DinersclubLogo} alt="diners club" height={24} />, type: CardType.DINERS_CLUB },
    ];
    return options
      ?.filter(option => {
        if (!values?.cardNumber?.length) return true;
        if (!cardType) return true; // type non détecté (saisie partielle) → afficher toutes les icônes
        return option.type === cardType;
      })
      .map(el => el.img);
  }, [cardType, values?.cardNumber]);

  const paymentMethods: PaymentMethodType[] = useMemo(
    () =>
      [
        {
          title: t('paymentMethods:creditAndDebitCards'),
          icons: creditCardIcons,
          component: (
            <PaymentMethodForm
              getFieldProps={getFieldProps}
              getFieldMeta={getFieldMeta}
              handleChange={handleChange}
              cardNumber={values.cardNumber}
              cardIcons={creditCardIcons}
            />
          ),
          type: 'CARD',
          specificType: 'CARD',
          hide: false,
        },
        {
          title: t('paymentMethods:googlePay'),
          icons: [<img src={GooglePayLogo} height={30} alt="Google pay" />],
          component: (
            <div>
              <Row justify="center">
                {t('paymentMethods:googlePayWindowOpen')}
                {/* <iframe src={`${googlePaymentResponse?.data.redirect}`}></iframe> */}
              </Row>
            </div>
          ),
          type: 'OTHER',
          specificType: 'google',
          hide: false,
        },
        {
          title: t('paymentMethods:applePay'),
          icons: [
            <span className="only-apple-device">{t('paymentMethods:onlyAppleDevices')}</span>,
            <img src={ApplePayLogo} height={24} alt="Apple pay" />,
          ],
          component: (
            <div>
              <Row justify="center">
                {t('paymentMethods:applePayWindowOpen')}
                {/* <iframe src={`${applePaymentResponse?.data.redirect}`}></iframe> */}
              </Row>
            </div>
          ),
          type: 'OTHER',
          specificType: 'apple',
          hide: false,
        },
        // {
        //   title: t('paymentMethods:paypal'),
        //   icons: [<img src={PaypalLogo} height={36} alt="Paypal" />],
        //   component: <div>This will contain content for Paypal</div>,
        //   type: 'OTHER',
        //   specificType: 'paypal',
        // },
        {
          // title: t('paymentMethods:gimapay'),
          title: t('paymentMethods:mobileMoney'),
          icons: [
            <img src={GimapayLogo} height={30} alt="Gimapay" />,
            <img src={MobileMoneyLogo} alt="" height={32} />,
          ],
          component: (
            <GimacForm
              loading={processingGimac}
              loadingCountries={loadingGimacCountries}
              gimacCountries={gimacCountries}
              getFieldProps={getFieldPropsGimac}
              getFieldMeta={getFieldMetaGimac}
              handleChange={handleChangeGimac}
              setFieldValue={setFieldValueGimac}
              setFieldTouched={setFieldTouchedGimac}
              values={valuesGimac}
              onOperatorDetected={setDetectedGimacOperator}
              waitingValidation={gimacWaitingValidation}
              headerIcons={[
                <img src={GimapayLogo} height={30} alt="Gimapay" key="gimapay" />,
                <img src={MobileMoneyLogo} alt="" height={32} key="mobilemoney" />,
              ]}
            />
          ),
          type: 'GIMAC',
          specificType: 'gimac',
          hide: false,
        },
        {
          title: t('paymentMethods:mobileMoney'),
          icons: [<img src={MobileMoneyLogo} alt="" height={32} />], // <img src={wave} alt="" height={30} />
          component: (
            <MobilePaymentForm
              paymentInfos={paymentInfos}
              countriesRefact={countriesRefact}
              // handleSubmit={handleSubmitMobilePaymentForm}
              loading={processingMobilePayment}
              gettingCountries={gettingCountries}
              countries={availableCountries}
              getFieldProps={getFieldPropsMobile}
              getFieldMeta={getFieldMetaMobile}
              handleChange={handleChangeMobile}
              setFieldValue={setFieldValue}
              setSelectedAvailableCountry={setSelectedAvailableCountry}
              valuesMobile={valuesMobile}
              setPhoneNumber={setPhoneNumber}
              setDialCode={setDialCode}
              isMobile={selectedMethod === 'mobile'}
              onMobileFormActivated={activateMobileMoneyOption}
            />
          ),
          type: 'MOBILE_MONEY',
          specificType: 'mobile',
          hide: false,
        },
        {
          title: t('paymentMethods:bank'),
          icons: [<BankIcon className={styles.icon} height={40} />],
          component: (
            <>
              {showOtp === false ? (
                <div>
                  {load === false && (
                    <BankPaymentMethodForm
                      getFieldProps={getFieldPropsBank}
                      getFieldMeta={getFieldMetaBank}
                      handleChange={handleChangeBank}
                      setFieldValue={setFieldValueBank}
                      setSelectedAvailableBank={setSelectedAvailableBank}
                      selectedAvailableBank={selectedAvailableBank}
                      gettingBank={gettingBank}
                      availableBank={availableBank}
                      showMessageInit={showMessageInit}
                      showChoiceAccount={showChoiceAccount}
                      setShowChoiceAccount={setShowChoiceAccount}
                      setShowButtonPay={setDisableButtonPay}
                    />
                  )}
                  {load === true && (
                    <div>
                      <Row justify="center">
                        <Loader />
                      </Row>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    {loadOtp === false && (
                      <BankOtpForm
                        getFieldProps={getFieldPropsOtpBank}
                        getFieldMeta={getFieldMetaOtpBank}
                        handleChange={handleChangeOtpBank}
                        showMessageOtp={showMessageOtp}
                        loading={loadOtp}
                        hideTimer={hideTimer}
                        setHideTimer={setHideTimer}
                        askNewOtp={askNewOtp}
                        toast={toast}
                        setShowButtonPay={setDisableButtonPay}
                        disableButtonValue={disableButtonPay}
                      />
                    )}
                    {loadOtp === true && (
                      <div>
                        <Row justify="center">
                          <Loader />
                        </Row>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          ),
          type: 'BANK',
          specificType: 'BANK',
          hide: false,
        },
      ].filter(method => !method.hide),
    [
      creditCardIcons,
      getFieldMeta,
      getFieldProps,
      t,
      googlePaymentResponse,
      paymentInfos,
      processingMobilePayment,
      getFieldPropsMobile,
      getFieldMetaMobile,
      gettingCountries,
      availableCountries,
      countriesRefact,
      getFieldMetaBank,
      getFieldPropsBank,
      gettingBank,
      availableBank,
      load,
      showOtp,
      showMessageInit,
      getFieldPropsOtpBank,
      getFieldMetaOtpBank,
      handleChangeOtpBank,
      showMessageOtp,
      loadOtp,
      hideTimer,
      setHideTimer,
      askNewOtp,
      toast,
      showChoiceAccount,
      setShowChoiceAccount,
      setDisableButtonPay,
      setDisableLoadingPay,
      activateMobileMoneyOption,
    ],
  );

  const displayedPaymentMethods = useMemo(
    () =>
      paymentMethods?.filter(p => {
        if (p.specificType === 'apple' && (!isIOS || !isMacOs) && !isSafari) return false;
        return paymentInfos?.data.meansOfPayment.some(d => d.type.toLowerCase() === p.type.toLowerCase());
      }) ?? [],
    [paymentMethods, paymentInfos?.data.meansOfPayment],
  );

  // Quand une méthode est sélectionnée depuis PaymentMethodSelection, n'afficher que celle-ci
  const methodsToDisplay = useMemo(() => {
    if (initialPaymentMethod) {
      return displayedPaymentMethods.filter(p => p.specificType === initialPaymentMethod);
    }
    return displayedPaymentMethods;
  }, [displayedPaymentMethods, initialPaymentMethod]);

  const initialPaymentMethodProcessedRef = useRef(false);

  useEffect(() => {
    if (displayedPaymentMethods.length === 0) return;
    const methodToSelect = initialPaymentMethod
      ? displayedPaymentMethods.find(p => p.specificType === initialPaymentMethod)?.specificType
      : displayedPaymentMethods[0]?.specificType;
    if (!methodToSelect || selectedMethod) return;
    setSelectedMethod(methodToSelect);
  }, [displayedPaymentMethods, initialPaymentMethod, selectedMethod]);

  // const filterPaymentMethods = useCallback(() => {
  //   const payMethods: PaymentMethodType[] = [];
  //   setFilteringPayMethods(true);

  //   for (let i = 0; i < Number(paymentInfos?.data.meansOfPayment.length); i++) {
  //     for (let j = 0; j < paymentMethods.length; j++) {
  //       if (paymentInfos?.data.meansOfPayment[i].type === paymentMethods[j].type) {
  //         payMethods.push(paymentMethods[j]);
  //         console.log(paymentMethods[j]);
  //         setFilteredPaymentMethods(payMethods);
  //       }
  //     }
  //   }
  //   setFilteringPayMethods(false);
  // }, [paymentInfos, paymentMethods, processingMobilePayment]);

  const selectMethod = (specificType: string) => {
    if (specificType === selectedMethod) return;
    setSelectedMethod(specificType);

    if (specificType === 'google') {
      processGooglePayment();
      setDisableButtonPay(false);
      setDisableLoadingPay(false);
    } else if (specificType === 'apple') {
      processApplePayment();
      setDisableButtonPay(false);
      setDisableLoadingPay(false);
    } else if (specificType === 'EUMM') {
      setDisableButtonPay(false);
      setDisableLoadingPay(false);
    } else if (specificType === 'CARD') {
      setDisableButtonPay(true);
      setDisableLoadingPay(false);
    } else if (specificType === 'mobile') {
      setDisableButtonPay(false);
      setDisableLoadingPay(false);
    } else if (specificType === 'gimac') {
      const hasPhone = valuesGimac?.phoneNumber && valuesGimac.phoneNumber.length >= 3;
      setDisableButtonPay(!hasPhone);
      setDisableLoadingPay(false);
    } else if (specificType === 'BANK') {
      setDisableButtonPay(true);
    }
  };

  const processGooglePayment = useCallback(async () => {
    const transactionRef = paymentInfos?.data?.transactionRef;
    const channelId = paymentInfos?.data?.meansOfPayment?.find(
      e => (e.type === 'OTHER' || e.name === 'OTHER') && e.active === true,
    )?.id;

    if (!transactionRef || !channelId) {
      message.error(t('home:global:generalResponse'), 3);
      return;
    }

    setProcessingGoogle(true);
    try {
      const res = await PaymentService.initAppleGooglePay(
        { transactionRef, channelId, paymentMethod: 'GooglePay' },
        'google',
      );
      setProcessingGoogle(false);
      setGooglePaymentResponse(res);
      const googleUrl = res?.data?.redirect;
      if (googleUrl) {
        window.location.href = googleUrl;
      } else {
        message.error(t('home:global:generalResponse'), 3);
      }
    } catch (error: any) {
      setProcessingGoogle(false);
      const exception = error.response?.data as GeneralException;
      let finalException;

      if (exception !== null && exception !== undefined && exception.description !== null) {
        finalException = exception.description;
      }
      if (process.env.NODE_ENV === 'development') {
        logger.debug('exception', { prefix: 'OrderDetails', data: exception });
      }
      message.error(finalException || t('home:global:generalResponse'), 3);
      // console.error(error);
    }
  }, [paymentInfos]);

  const processApplePayment = useCallback(async () => {
    const transactionRef = paymentInfos?.data?.transactionRef;
    const channelId = paymentInfos?.data?.meansOfPayment?.find(
      e => (e.type === 'OTHER' || e.name === 'OTHER') && e.active === true,
    )?.id;

    if (!transactionRef || !channelId) {
      message.error(t('home:global:generalResponse'), 3);
      return;
    }

    setProcessingApple(true);
    try {
      const res = await PaymentService.initAppleGooglePay(
        { transactionRef, channelId, paymentMethod: 'ApplePay' },
        'apple',
      );
      setProcessingApple(false);
      setApplePaymentResponse(res);
      const appleUrl = res?.data?.redirect;
      if (appleUrl) {
        window.location.href = appleUrl;
      } else {
        message.error(t('home:global:generalResponse'), 3);
      }
    } catch (error: any) {
      setProcessingApple(false);
      const exception = error.response?.data as GeneralException;
      let finalException;

      if (exception !== null && exception !== undefined && exception.description !== null) {
        finalException = exception.description;
      }
      message.error(finalException || t('home:global:generalResponse'), 3);
      // console.error(error);
    }
  }, [paymentInfos]);

  useEffect(() => {
    if (!initialPaymentMethod || displayedPaymentMethods.length === 0) return;
    const found = displayedPaymentMethods.some(p => p.specificType === initialPaymentMethod);
    if (!found) return;

    if (initialPaymentMethod === 'google' && !initialPaymentMethodProcessedRef.current) {
      initialPaymentMethodProcessedRef.current = true;
      processGooglePayment();
      setDisableButtonPay(false);
      setDisableLoadingPay(false);
    } else if (initialPaymentMethod === 'apple' && !initialPaymentMethodProcessedRef.current) {
      initialPaymentMethodProcessedRef.current = true;
      processApplePayment();
      setDisableButtonPay(false);
      setDisableLoadingPay(false);
    } else if (['EUMM', 'mobile'].includes(initialPaymentMethod)) {
      setDisableButtonPay(false);
      setDisableLoadingPay(false);
    } else if (initialPaymentMethod === 'CARD') {
      setDisableButtonPay(true);
      setDisableLoadingPay(false);
    } else if (initialPaymentMethod === 'gimac') {
      const hasPhone = valuesGimac?.phoneNumber && valuesGimac.phoneNumber.length >= 3;
      setDisableButtonPay(!hasPhone);
      setDisableLoadingPay(false);
    } else if (initialPaymentMethod === 'BANK') {
      setDisableButtonPay(true);
    }
  }, [
    initialPaymentMethod,
    displayedPaymentMethods,
    processGooglePayment,
    processApplePayment,
    valuesGimac?.phoneNumber,
  ]);

  // const handleBootSubmits = useCallback((e: any) => {

  // }, []);

  useEffect(() => {
    // filterPaymentMethods();
    // console.log('appleDevice', regex.isAppleDevice());
    initCardData();
    initMobileData();
    // initBankData();
    handleGetAvailableCountries();
    handleGetAvailableBanks();
    handleGetGimacCountries();
    // window.parent.postMessage(window.location.href, 'http://localhost:3000');
    // window.addEventListener('message', handleIframeMessage);

    // return () => {
    //   window.removeEventListener('message', handleIframeMessage);
    // };
  }, [paymentInfos]);
  return (
    <form
      onSubmit={e => {
        if (selectedMethod === 'gimac') {
          handleFormSubmitGimac(e);
          return;
        }

        if (selectedMethod === 'mobile') {
          handleFormSubmitMobile(e);
          return;
        }

        if (selectedMethod === 'BANK') {
          if (showOtp === false) {
            handleFormSubmitBank(e);
          }
          if (showOtp === true) {
            handleFormSubmitOtpBank(e);
          }
          return;
        }

        if (selectedMethod === 'EUMM') {
          handleFormSubmitExpressUnion(e);
          return;
        }

        handleFormSubmitCard(e);
        // paymentMethodSpecType === 'CARD'
        //   ? handleFormSubmitCard
        //   : paymentMethodSpecType === 'mobile'
        //   ? handleFormSubmitMobile
        //   : undefined;
      }}
    >
      <div className={styles.align_sections}>
        {
          // filteringPayMethods ? (
          //   <Row justify="center">
          //     <Loader />
          //   </Row>
          // ) :
          <div className={styles.methodSection}>
            {/* {onBackToMethodSelection && (
              <button type="button" className={styles.backButton} onClick={onBackToMethodSelection}>
                ← {t('checkoutPage:changePaymentMethod')}
              </button>
            )} */}
            {false && onBackToMethodSelection && (
              <button type="button" className={styles.backButton} onClick={onBackToMethodSelection}>
                ← {t('checkoutPage:changePaymentMethod')}
              </button>
            )}
            <Text weight={FontWeight.SEMIBOLD} className={styles.title}>
              {t('checkoutPage:selectPaymentMethod')}
            </Text>
            <div className={styles.accordion_wrapper}>
              {methodsToDisplay.map(paymentMethod => {
                const isActive = selectedMethod === paymentMethod.specificType;
                const showLoader =
                  (paymentMethod.specificType === 'google' && processingGoogle) ||
                  (paymentMethod.specificType === 'apple' && processingApple);
                const isSingleFromSelection = initialPaymentMethod && methodsToDisplay.length === 1;

                return (
                  <div key={paymentMethod.specificType} className={styles.accordion_item}>
                    {!isSingleFromSelection && (
                      <div
                        role="button"
                        tabIndex={0}
                        className={styles.accordion_title}
                        onClick={() => selectMethod(paymentMethod.specificType)}
                        onKeyDown={e => e.key === 'Enter' && selectMethod(paymentMethod.specificType)}
                      >
                        <span className={styles.accordion_title_text}>
                          <span className={styles.radioVisualOnly}>
                            <Radio checked={isActive} />
                          </span>
                          {paymentMethod.title}
                        </span>
                        <span className={styles['icons-wrapper']}>
                          {paymentMethod.icons?.map((icon, index) => (
                            <span key={index} className={styles.icon}>
                              {icon}
                            </span>
                          ))}
                        </span>
                      </div>
                    )}
                    {(isActive || isSingleFromSelection) && (
                      <div className={styles.accordion_content}>
                        {showLoader ? (
                          <Row justify="center">
                            <Loader />
                          </Row>
                        ) : (
                          <Card slightlyRounded className={styles.form_wrapper}>
                            {paymentMethod.component}
                          </Card>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        }
        <div>
          <Text weight={FontWeight.SEMIBOLD} className={styles.title}>
            {t('checkoutPage:orderSummary')}
          </Text>
          <OrderSummaryCardComponent
            paymentInfos={paymentInfos}
            loading={loading}
            isMobile={selectedMethod === 'mobile'}
            phoneNumber={phoneNumber}
            dialCode={dialCode}
            dataTransaction={dataTransaction}
            loadingDataTr={loadingDataTr}
            actionDisable={selectedMethod === 'CARD' ? !(isValid || isCardFormValid) : disableButtonPay}
            actionLoading={disableLoadingPay}
          />
        </div>
      </div>
    </form>
  );
};
